/**
 * SendStatementsOptionsModal
 *
 * Step 1 of the 3-step Send Statements flow.
 *
 * Allows the user to configure statement parameters before proceeding to
 * the Send Email compose step (Step 2 – BulkEmailModal / CustomerSendEmailModal).
 *
 * Fields:
 *  - Statement Date        (defaults to today, editable)
 *  - Currency Values       (Customer Currency | Base Currency)
 *  - Exclude Disputed      (toggle, default off)
 *  - Statement Message     (pre-filled from statement template, editable)
 *
 * Buttons: Preview | Cancel | Next
 *
 * Guards:
 *  - If no statement template exists for the company, Next is disabled with
 *    a blocking message directing the user to Settings > Templates.
 */

import { useState, useEffect } from "react";
import { pdf } from "@react-pdf/renderer";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { DatePicker } from "@/components/ui/date-picker";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Eye, ArrowRight, FileText, Users } from "lucide-react";
import { toast } from "sonner";
import { useTemplatesList, useTemplateById } from "@/hooks/useTemplatesData";
import { StatementDocument } from "@/lib/react-pdf/StatementDocument";
import { buildSampleData, buildCustomerPreviewData, reconcileBuckets, resolveStatementCurrency, type CustomerPreviewInput } from "@/lib/react-pdf/statement-data";
import type { TemplateBuilderConfig } from "@/types/template-builder";
import { useCompany } from "@/contexts/CompanyContext";
import type { Template } from "@/types/template";
import { fetchOutstandingTransactions, fetchCustomerAging, fetchLatestReceipt, fetchAgingBandsConfig, resolveStatementLogoUrl } from "@/lib/fetch-email-transactions";
import { useCompanyWithProfile } from "@/hooks/useCompanyWithProfile";
import { useApi } from "@/hooks/useApi";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SendStatementOptions {
  statementDate: Date;
  currencyMode: "customer" | "base";
  excludeDisputed: boolean;
  statementMessage: string;
  statementTemplate: Template | null;
  /** Full template detail including pdfSettings — null until template detail loads */
  fullTemplate: Template | null;
}

export interface SendStatementsOptionsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** All customers selected in the grid */
  selectedCustomers: (CustomerPreviewInput & { id: string })[];
  /** Called with the configured options when user clicks Next */
  onNext: (options: SendStatementOptions) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function SendStatementsOptionsModal({
  open,
  onOpenChange,
  selectedCustomers,
  onNext,
}: Readonly<SendStatementsOptionsModalProps>) {
  const selectedCount = selectedCustomers.length;
  const previewCustomer = selectedCustomers[0] ?? null;
  // ── State ─────────────────────────────────────────────────────────────────
  const [statementDate, setStatementDate] = useState<Date>(new Date());
  const [currencyMode, setCurrencyMode] = useState<"customer" | "base">("customer");
  const [excludeDisputed, setExcludeDisputed] = useState(false);
  const [statementMessage, setStatementMessage] = useState("Thank you for your business.");
  const [isPreviewing, setIsPreviewing] = useState(false);

  // ── Company ───────────────────────────────────────────────────────────────
  const { selectedCompany } = useCompany();
  const { apiBaseUrl, getHeaders } = useApi();
  const { data: companyWithProfile } = useCompanyWithProfile(selectedCompany?.id);
  const companyLogoUrl = companyWithProfile?.details?.logoUrl ?? selectedCompany?.logoUrl ?? null;

  // ── Templates ─────────────────────────────────────────────────────────────
  const { data: allTemplates = [], isLoading: templatesLoading } = useTemplatesList();
  const statementTemplates = allTemplates.filter((t) => t.type === "statement");
  const statementTemplate = statementTemplates.find((t) => t.isDefault) ?? null;
  const hasAnyStatementTemplate = statementTemplates.length > 0;
  const hasStatementTemplate = statementTemplate !== null;
  // Fetch full detail (includes pdfSettings) — list endpoint only returns summary data
  const { data: fullTemplate, isLoading: templateDetailLoading } = useTemplateById(statementTemplate?.id ?? null);

  // Pre-populate the statement message from the template when it becomes available
  useEffect(() => {
    if (statementTemplate && !statementMessage) {
      // Use the template content as the default message (strip HTML tags for the text area)
      const stripped = statementTemplate.content
        ? statementTemplate.content.replace(/<[^>]*>/g, "").trim()
        : "Thank you for your business.";
      setStatementMessage(stripped);
    }
    // Only run when the template loads for the first time
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statementTemplate?.id]);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setStatementDate(new Date());
      setCurrencyMode("customer");
      setExcludeDisputed(false);
      setStatementMessage("");
      setIsPreviewing(false);
    }
  }, [open]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleCancelClick = () => {
    onOpenChange(false);
  };

  const handlePreview = async () => {
    if (!fullTemplate) return;
    const ps = fullTemplate.pdfSettings;
    if (!ps?.layoutSettings || !ps.contentSettings || !ps.fieldsSettings) {
      toast.error("Template is missing PDF settings. Please re-save it in the template builder.");
      return;
    }
    setIsPreviewing(true);
    try {
      const { logoUrl: resolvedLogoUrl, logoSource } = await resolveStatementLogoUrl({
        pdfSettings: ps,
        companyLogoUrl,
        apiBaseUrl,
        companyId: selectedCompany?.id,
        getHeaders,
      });

      const apiConfig = { apiBaseUrl, getHeaders };
      const previewAgingCurrency = previewCustomer ? resolveStatementCurrency(previewCustomer, currencyMode) : undefined;
      const [previewTransactions, previewAging, previewReceipt, previewAgingBands] = previewCustomer?.id
        ? await Promise.all([
            fetchOutstandingTransactions(apiConfig, previewCustomer.id, excludeDisputed),
            fetchCustomerAging(apiConfig, previewCustomer.id, previewAgingCurrency),
            fetchLatestReceipt(apiConfig, previewCustomer.id),
            fetchAgingBandsConfig(apiConfig),
          ])
        : [[], null, null, undefined];

      const config: TemplateBuilderConfig = {
        layoutSettings: ps.layoutSettings,
        contentSettings: {
          ...ps.contentSettings,
          closingText: { enabled: true, content: statementMessage },
        },
        fieldsSettings: previewAgingBands
          ? {
              ...ps.fieldsSettings,
              agingSummary: {
                ...ps.fieldsSettings.agingSummary,
                buckets: reconcileBuckets(ps.fieldsSettings.agingSummary.buckets, previewAgingBands),
              },
            }
          : ps.fieldsSettings,
        primaryColor: ps.styling?.primaryColor ?? "#1a5c5c",
        documentFont: ps.styling?.fontFamily ?? "Helvetica",
        logoUrl: resolvedLogoUrl,
        logoSize: ps.logoSize ?? "medium",
        logoSource,
        companyName: selectedCompany?.name ?? ps.companyName ?? "",
        companyEmail: selectedCompany?.email ?? companyWithProfile?.details?.email ?? undefined,
        companyPhone: selectedCompany?.phone ?? companyWithProfile?.details?.phone ?? undefined,
        companyReg: selectedCompany?.companyRegistrationNumber ?? companyWithProfile?.details?.companyRegistrationNumber ?? undefined,
        companyVat: selectedCompany?.vatRegistrationNumber ?? companyWithProfile?.details?.vatNumber ?? undefined,
        companyAddress: companyWithProfile?.details?.addressLine ?? undefined,
        companyCountry: companyWithProfile?.details?.country ?? undefined,
        companyBankName: companyWithProfile?.bankDetails?.[0]?.accountName ?? undefined,
        sortCode: companyWithProfile?.bankDetails?.[0]?.sortCode ?? undefined,
        bankAccountNumber: companyWithProfile?.bankDetails?.[0]?.accountNumber ?? undefined,
        ibanNumber: companyWithProfile?.bankDetails?.[0]?.ibanNumber ?? undefined,
        swiftCode: companyWithProfile?.bankDetails?.[0]?.swiftBicCode ?? undefined,
      };
      const enrichedPreviewCustomer = previewCustomer
        ? { ...previewCustomer, lastPaymentAmount: previewReceipt?.amount, lastPaymentCurrencyAmount: previewReceipt?.currencyAmount }
        : undefined;
      const data = enrichedPreviewCustomer
        ? buildCustomerPreviewData(config, enrichedPreviewCustomer, previewTransactions, previewAging ?? null, statementDate, currencyMode, previewAgingBands)
        : buildSampleData(config, previewAgingBands);
      const blob = await pdf(<StatementDocument config={config} data={data} />).toBlob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      toast.error("Failed to generate statement preview.");
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleNext = () => {
    if (!hasStatementTemplate) return;
    onNext({
      statementDate,
      currencyMode,
      excludeDisputed,
      statementMessage,
      statementTemplate,
      fullTemplate: fullTemplate ?? null,
    });
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <DialogTitle className="text-lg font-semibold">
                Send Statements
              </DialogTitle>
              <DialogDescription className="mt-1">
                Step 1 of 4 — Configure statement options
              </DialogDescription>
            </div>
            {/* Customer count indicator */}
            <Badge variant="secondary" className="flex items-center gap-1.5 px-3 py-1.5 text-sm shrink-0">
              <Users className="h-3.5 w-3.5" />
              {selectedCount} {selectedCount === 1 ? "customer" : "customers"} selected
            </Badge>
          </div>
        </DialogHeader>

        <div className="space-y-5 py-2">

          {/* No statement template warning */}
          {!templatesLoading && !hasAnyStatementTemplate && (
            <Alert variant="destructive">
              <AlertDescription>
                No statement template has been set up for this company. Please go to{" "}
                <strong>Settings &gt; Templates</strong> to create a statement template before
                sending statements.
              </AlertDescription>
            </Alert>
          )}

          {/* No default template warning */}
          {!templatesLoading && hasAnyStatementTemplate && !hasStatementTemplate && (
            <Alert variant="destructive">
              <AlertDescription>
                No default statement template has been set. Please go to{" "}
                <strong>Settings &gt; Templates</strong> and set a template as default before
                sending statements.
              </AlertDescription>
            </Alert>
          )}

          {/* Statement template indicator */}
          {hasStatementTemplate && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <FileText className="h-4 w-4 shrink-0 text-primary" />
              <span>
                Using default template:{" "}
                <span className="font-medium text-foreground">{statementTemplate.name}</span>
              </span>
            </div>
          )}

          {/* Statement Date */}
          <div className="space-y-1.5">
            <Label htmlFor="statement-date">Statement Date</Label>
            <DatePicker
              date={statementDate}
              onDateChange={(d) => setStatementDate(d ?? new Date())}
              placeholder="Select statement date"
              dateFormat="dd/MM/yyyy"
            />
          </div>

          {/* Currency Values */}
          <div className="space-y-1.5">
            <Label htmlFor="currency-values">Currency Values</Label>
            <Select
              value={currencyMode}
              onValueChange={(v) => {
                if (v === "customer" || v === "base") {
                  setCurrencyMode(v);
                }
              }}
            >
              <SelectTrigger id="currency-values" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="customer">Customer Currency</SelectItem>
                <SelectItem value="base">Base Currency</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Exclude Disputed Transactions */}
          <div className="flex items-center justify-between rounded-lg border px-4 py-3">
            <div className="space-y-0.5">
              <Label htmlFor="exclude-disputed" className="text-sm font-medium cursor-pointer">
                Exclude Disputed Transactions
              </Label>
              <p className="text-xs text-muted-foreground">
                Disputed transactions will be omitted from the statement
              </p>
            </div>
            <Switch
              id="exclude-disputed"
              checked={excludeDisputed}
              onCheckedChange={setExcludeDisputed}
            />
          </div>

          {/* Statement Message */}
          <div className="space-y-1.5">
            <Label htmlFor="statement-message">Statement Closing Message</Label>
            <Textarea
              id="statement-message"
              placeholder="Enter a message to include on the statement…"
              value={statementMessage}
              onChange={(e) => setStatementMessage(e.target.value)}
              className="min-h-[100px] resize-none"
            />
            <p className="text-xs text-muted-foreground">
              Pre-filled from the statement template. You can edit this message before sending.
            </p>
          </div>
        </div>

        <DialogFooter className="flex items-center justify-between gap-2 sm:justify-between">
          {/* Left side: Preview */}
          <Button
            variant="outline"
            onClick={handlePreview}
            disabled={!hasStatementTemplate || templateDetailLoading || isPreviewing}
            className="gap-2"
          >
            <Eye className="h-4 w-4" />
            {isPreviewing ? "Generating…" : "Preview"}
          </Button>

          {/* Right side: Cancel + Next */}
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={handleCancelClick}>
              Cancel
            </Button>
            <Button
              onClick={handleNext}
              disabled={!hasStatementTemplate || templatesLoading}
              className="gap-2"
            >
              Next
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
